import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { GraduationCap, Plus, X, ExternalLink, Clock, CheckCircle2, AlertCircle, Star, Users, Plane, Search, Pencil, Trash2 } from "lucide-react";

const CATEGORIES = ["compliance", "technical", "leadership", "safety", "soft_skills", "onboarding", "other"];
const TYPE_LABEL = {
  mandatory: { label: "train.type_mandatory", color: "bg-[#e5c1c1] text-[#881a27]" },
  suggested: { label: "train.type_suggested", color: "bg-[#fef3c7] text-[#92400e]" },
  manager_suggested: { label: "train.mgr_suggested", color: "bg-[#e2e8f0] text-[#475569]" },
};

function CatalogTab({ canManage, onAssign }) {
  const { t: tr } = useI18n();
  const [trainings, setTrainings] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [editing, setEditing] = useState(null);
  const [showDetail, setShowDetail] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (filterCat !== "all") params.set("category", filterCat);
    const r = await api.get(`/trainings?${params.toString()}`);
    setTrainings(r.data);
  };
  useEffect(() => { load(); }, [filterCat]); // eslint-disable-line

  const remove = async (t) => {
    if (!window.confirm(tr("train.delete_confirm", { name: t.name }))) return;
    await api.delete(`/trainings/${t.id}`);
    load();
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setFilterCat("all")} className={`px-3 py-1 rounded-[10px] text-[11px] ${filterCat === "all" ? "bg-[#475569] text-white" : "bg-white text-[#747474] border border-[#eeeded]"}`}>{tr("common.all")}</button>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setFilterCat(c)} className={`px-3 py-1 rounded-[10px] text-[11px] capitalize ${filterCat === c ? "bg-[#475569] text-white" : "bg-white text-[#747474] border border-[#eeeded]"}`}>{tr(`train.cat_${c}`)}</button>
          ))}
        </div>
        {canManage && <button data-testid="new-training-btn" onClick={() => setEditing({})} className="sw-btn-primary"><Plus size={14} />  {tr("train.new")}</button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {trainings.map((t) => (
          <div
            key={t.id}
            data-testid={`training-card-${t.id}`}
            onClick={() => setShowDetail(t)}
            className="sw-card !p-4 flex flex-col gap-2 cursor-pointer hover:border-[#475569] hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[#475569]">{tr(`train.cat_${t.category}`)}</div>
                <div className="font-medium text-[14px] text-[#323232] mt-1">{t.name}</div>
                <div className="text-[11px] text-[#747474] mt-1 line-clamp-2">{t.description}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-[#747474]">
              <Clock size={11} /> {t.duration_minutes} min
              {t.mandatory_for_roles?.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-[5px] bg-[#e5c1c1] text-[#881a27]">{tr("train.mandatory_for")} {t.mandatory_for_roles.join(", ")}</span>}
            </div>
            <div className="flex items-center justify-between mt-1 pt-2 border-t border-[#f6f6f6]">
              {t.external_url && <a href={t.external_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#475569] text-[11px] hover:underline"><ExternalLink size={9} className="inline" />  {tr("train.open_course")}</a>}
              <div className="flex gap-1 ml-auto" onClick={(e) => e.stopPropagation()}>
                {canManage && <button data-testid={`assign-training-${t.id}`} onClick={() => onAssign(t)} className="sw-btn-secondary !text-[11px] !px-2 !py-0.5"><Users size={11} className="inline mr-1" />{tr("train.assign")}</button>}
                {canManage && <button data-testid={`edit-training-${t.id}`} onClick={() => setEditing(t)} className="text-[#747474] hover:text-[#475569]"><Pencil size={12} /></button>}
                {canManage && <button onClick={() => remove(t)} className="text-[#747474] hover:text-[#881a27]"><Trash2 size={12} /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <TrainingModal training={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {showDetail && <TrainingDetailModal training={showDetail} onClose={() => setShowDetail(null)} />}
    </div>
  );
}

function TrainingModal({ training, onClose, onSaved }) {
  const { t: tr } = useI18n();
  const isNew = !training.id;
  const [form, setForm] = useState({
    name: training.name || "",
    description: training.description || "",
    category: training.category || "other",
    duration_minutes: training.duration_minutes || 60,
    external_url: training.external_url || "",
    mandatory_for_roles: training.mandatory_for_roles || [],
    eligibility_rules: training.eligibility_rules || { departments: [], contract_types: [], locations: [], min_tenure_days: null },
    bonus_eligible: !!training.bonus_eligible,
    bonus_program: training.bonus_program || "",
    active: training.active !== false,
  });
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const ROLES = ["employee", "manager", "hr", "ceo"];
  const CONTRACTS = ["CLT_FrankCrum", "PJ_Lummar", "PJ_Direct"];
  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/users").then((r) => {
      const locs = Array.from(new Set(r.data.map((u) => u.location).filter(Boolean)));
      setLocations(locs);
    }).catch(() => {});
  }, []);
  const toggleRole = (r) => setForm({ ...form, mandatory_for_roles: form.mandatory_for_roles.includes(r) ? form.mandatory_for_roles.filter((x) => x !== r) : [...form.mandatory_for_roles, r] });
  const toggleElig = (k, v) => {
    const cur = form.eligibility_rules[k] || [];
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    setForm({ ...form, eligibility_rules: { ...form.eligibility_rules, [k]: next } });
  };
  const save = async () => {
    try {
      const payload = { ...form };
      payload.eligibility_rules = {
        ...form.eligibility_rules,
        min_tenure_days: form.eligibility_rules.min_tenure_days ? parseInt(form.eligibility_rules.min_tenure_days) : null,
      };
      if (isNew) await api.post("/trainings", payload);
      else await api.patch(`/trainings/${training.id}`, payload);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[640px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between mb-4"><h3 className="sw-heading text-[18px]">{isNew ? "New" : "Edit"} training</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="md:col-span-2"><label className="sw-label">Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" data-testid="training-name" /></div>
          <div className="md:col-span-2"><label className="sw-label">Description</label><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" /></div>
          <div><label className="sw-label">Category *</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="sw-input">{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className="sw-label">Duration (min) *</label><input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 0 })} className="sw-input" /></div>
          <div className="md:col-span-2"><label className="sw-label">External URL (LMS / course link)</label><input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://..." className="sw-input" data-testid="training-url" /></div>
          <div className="md:col-span-2">
            <label className="sw-label">{tr("train.mandatory_roles")}</label>
            <div className="flex flex-wrap gap-2">
              {ROLES.map((r) => <button key={r} type="button" onClick={() => toggleRole(r)} className={`px-3 py-1 rounded-[8px] text-[11px] capitalize ${form.mandatory_for_roles.includes(r) ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{r}</button>)}
            </div>
          </div>

          <div className="md:col-span-2 mt-2 border-t border-[#e2e8f0] pt-4">
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569] mb-3">Additional eligibility (beyond mandatory)</div>
            <div className="text-[11px] text-[#94a3b8] mb-3">Trainings can also be assignable to users matching specific filters — used for bonus programs and conditional curriculums.</div>

            <label className="sw-label">Departments</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {departments.map((d) => (
                <button key={d.id} type="button" data-testid={`elig-dept-${d.id}`} onClick={() => toggleElig("departments", d.id)} className={`px-3 py-1 rounded-[8px] text-[11px] ${form.eligibility_rules.departments?.includes(d.id) ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{d.name}</button>
              ))}
            </div>

            <label className="sw-label">Contract types</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {CONTRACTS.map((c) => (
                <button key={c} type="button" data-testid={`elig-contract-${c}`} onClick={() => toggleElig("contract_types", c)} className={`px-3 py-1 rounded-[8px] text-[11px] ${form.eligibility_rules.contract_types?.includes(c) ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{tr(`train.cat_${c}`)}</button>
              ))}
            </div>

            {locations.length > 0 && (
              <>
                <label className="sw-label">Locations</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {locations.map((l) => (
                    <button key={l} type="button" data-testid={`elig-location-${l}`} onClick={() => toggleElig("locations", l)} className={`px-3 py-1 rounded-[8px] text-[11px] ${form.eligibility_rules.locations?.includes(l) ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{l}</button>
                  ))}
                </div>
              </>
            )}

            <label className="sw-label">Minimum tenure (days, optional)</label>
            <input
              type="number"
              min="0"
              data-testid="elig-min-tenure"
              value={form.eligibility_rules.min_tenure_days || ""}
              onChange={(e) => setForm({ ...form, eligibility_rules: { ...form.eligibility_rules, min_tenure_days: e.target.value || null } })}
              className="sw-input"
              placeholder={tr("train.min_days_ph")}
            />
          </div>

          <div className="md:col-span-2 mt-2 border-t border-[#e2e8f0] pt-4">
            <label className="flex items-center gap-2 text-[13px] text-[#1e293b] cursor-pointer">
              <input type="checkbox" data-testid="bonus-eligible" checked={form.bonus_eligible} onChange={(e) => setForm({ ...form, bonus_eligible: e.target.checked })} />
              This training gates a bonus program
            </label>
            {form.bonus_eligible && (
              <input
                data-testid="bonus-program"
                value={form.bonus_program}
                onChange={(e) => setForm({ ...form, bonus_program: e.target.value })}
                placeholder="Bonus program label (e.g. Q3-2026 Safety Bonus)"
                className="sw-input mt-2"
              />
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="sw-btn-secondary">{tr("common.cancel")}</button><button data-testid="save-training-btn" onClick={save} disabled={!form.name} className="sw-btn-primary">{tr("common.save")}</button></div>
      </div>
    </div>
  );
}

function AssignModal({ training, onClose, onSaved }) {
  const { t: tr } = useI18n();
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [mode, setMode] = useState("user");  // user | department | role
  const [form, setForm] = useState({ user_id: "", department_id: "", target_role: "", type: "suggested", due_date: "", manager_note: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active !== false)));
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { training_id: training.id, type: form.type, due_date: form.due_date || null, manager_note: form.manager_note || null };
      if (mode === "user") payload.user_id = form.user_id;
      else if (mode === "department") payload.department_id = form.department_id;
      else payload.target_role = form.target_role;
      const r = await api.post("/training-assignments", payload);
      alert(`${r.data.created} assignment(s) created.`);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[520px] w-full p-6 shadow-xl">
        <div className="flex justify-between mb-1"><h3 className="sw-heading text-[18px]">{tr("train.assign_title")} {training.name}</h3><button onClick={onClose}><X size={20} /></button></div>
        <p className="text-[12px] text-[#747474] mb-4">Create a learning trail by assigning trainings to individuals, departments, or roles.</p>
        <div className="flex gap-2 mb-3">
          {["user", "department", "role"].map((m) => <button key={m} onClick={() => setMode(m)} data-testid={`assign-mode-${m}`} className={`px-3 py-1.5 rounded-[10px] text-[12px] capitalize ${mode === m ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{m}</button>)}
        </div>
        <div className="grid grid-cols-1 gap-3 mb-4">
          {mode === "user" && <div><label className="sw-label">{tr("common.employee")} *</label><select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="sw-input" data-testid="assign-user"><option value="">—</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}</select></div>}
          {mode === "department" && <div><label className="sw-label">Department *</label><select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="sw-input"><option value="">—</option>{depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>}
          {mode === "role" && <div><label className="sw-label">Role *</label><select value={form.target_role} onChange={(e) => setForm({ ...form, target_role: e.target.value })} className="sw-input"><option value="">—</option><option value="employee">{tr("common.employee")}</option><option value="manager">{tr("common.manager")}</option><option value="hr">HR</option><option value="ceo">CEO</option></select></div>}
          <div><label className="sw-label">Type *</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="sw-input" data-testid="assign-type">
              <option value="mandatory">Mandatory</option><option value="suggested">Suggested</option><option value="manager_suggested">{tr("train.mgr_suggested")}</option>
            </select>
          </div>
          <div><label className="sw-label">{tr("train.due_date")}</label><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="sw-input" /></div>
          <div><label className="sw-label">{tr("train.note_emp")}</label><textarea rows={2} value={form.manager_note} onChange={(e) => setForm({ ...form, manager_note: e.target.value })} className="sw-input" /></div>
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="sw-btn-secondary">{tr("common.cancel")}</button><button data-testid="save-assign-btn" onClick={save} disabled={saving} className="sw-btn-primary">{saving ? "..." : tr("train.assign")}</button></div>
      </div>
    </div>
  );
}

function MyTrainingsTab() {
  const { t: tr } = useI18n();
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [completing, setCompleting] = useState(null);
  const load = async () => {
    const params = new URLSearchParams();
    if (filterStatus !== "all") params.set("status", filterStatus);
    params.set("mine", "true");
    const r = await api.get(`/training-assignments?${params.toString()}`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [filterStatus]); // eslint-disable-line
  const start = async (a) => {
    await api.post(`/training-assignments/${a.id}/start`);
    if (a.training_url) window.open(a.training_url, "_blank");
    load();
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1 flex-wrap">
        {["all", "pending", "in_progress", "completed"].map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)} data-testid={`mytraining-${s}`} className={`px-3 py-1.5 rounded-[10px] text-[11px] ${filterStatus === s ? "bg-[#475569] text-white" : "bg-white text-[#747474] border border-[#eeeded]"}`}>{s.replace("_", " ")}</button>
        ))}
      </div>
      {items.length === 0 ? <div className="sw-card text-center py-12 text-[#747474]">{tr("train.no_assignments")}</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((a) => {
            const t = TYPE_LABEL[a.type] || TYPE_LABEL.suggested;
            const overdue = a.status !== "completed" && a.due_date && new Date(a.due_date) < new Date();
            return (
              <div key={a.id} className="sw-card !p-4 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider"><span className={`px-1.5 py-0.5 rounded-[5px] ${t.color}`}>{tr(t.label)}</span><span className="text-[#475569]">{tr(`train.cat_${a.training_category}`)}</span></div>
                    <div className="font-medium text-[14px] text-[#323232] mt-1">{a.training_name}</div>
                    {a.manager_note && <div className="text-[11px] text-[#475569] italic mt-1">📝 {a.manager_note}</div>}
                  </div>
                  {a.status === "completed" ? <CheckCircle2 size={16} className="text-[#008932]" /> : overdue ? <AlertCircle size={16} className="text-[#881a27]" /> : null}
                </div>
                <div className="flex items-center justify-between text-[11px] text-[#747474]">
                  <span><Clock size={11} className="inline" /> {a.duration_minutes} min · by {a.assigned_by_name}</span>
                  {a.due_date && <span className={overdue ? "text-[#881a27] font-medium" : ""}>Due {a.due_date}</span>}
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-[#f6f6f6] mt-1">
                  {a.status === "pending" && <button data-testid={`start-${a.id}`} onClick={() => start(a)} className="sw-btn-primary !text-[11px] !px-3 !py-1">Start course →</button>}
                  {a.status === "in_progress" && <>
                    {a.training_url && <a href={a.training_url} target="_blank" rel="noopener noreferrer" className="sw-btn-secondary !text-[11px] !px-3 !py-1"><ExternalLink size={11} /> Resume</a>}
                    <button data-testid={`complete-${a.id}`} onClick={() => setCompleting(a)} className="sw-btn-primary !text-[11px] !px-3 !py-1">Mark complete</button>
                  </>}
                  {a.status === "completed" && <span className="text-[11px] text-[#008932]">✓ Completed {a.completion_date?.slice(0,10)} {a.score ? `· ${a.score}/100` : ""}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {completing && <CompleteModal assignment={completing} onClose={() => setCompleting(null)} onSaved={() => { setCompleting(null); load(); }} />}
    </div>
  );
}

function CompleteModal({ assignment, onClose, onSaved }) {
  const { t: tr } = useI18n();
  const [form, setForm] = useState({ completion_url: "", score: "", notes: "" });
  const save = async () => {
    try { await api.post(`/training-assignments/${assignment.id}/complete`, { ...form, score: form.score ? parseFloat(form.score) : null }); onSaved(); }
    catch (err) { alert(err?.response?.data?.detail || "Error"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[480px] w-full p-6 shadow-xl">
        <div className="flex justify-between mb-3"><h3 className="sw-heading text-[18px]">Mark complete</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="text-[12px] text-[#747474] mb-3">{assignment.training_name}</div>
        <div className="grid grid-cols-1 gap-3 mb-4">
          <div><label className="sw-label">Certificate / Completion URL</label><input placeholder="https://..." value={form.completion_url} onChange={(e) => setForm({ ...form, completion_url: e.target.value })} className="sw-input" /></div>
          <div><label className="sw-label">Score (0-100, optional)</label><input type="number" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} className="sw-input" /></div>
          <div><label className="sw-label">Notes</label><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sw-input" /></div>
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="sw-btn-secondary">{tr("common.cancel")}</button><button data-testid="confirm-complete-btn" onClick={save} className="sw-btn-primary">Complete</button></div>
      </div>
    </div>
  );
}

function ComplianceTab() {
  const { t: tr } = useI18n();
  const [summary, setSummary] = useState([]);
  useEffect(() => { api.get("/trainings/compliance-summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);
  return (
    <div className="flex flex-col gap-3">
      <div className="text-[13px] text-[#747474]">% of users in each role who have completed all mandatory trainings for that role.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {summary.length === 0 ? <div className="sw-card text-center py-12 text-[#747474] md:col-span-2">{tr("train.no_mandatory")}</div> : summary.map((s) => (
          <div key={s.role} className="sw-card !p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-[15px] capitalize">{s.role}</div>
              <div className={`text-[20px] font-bold ${s.compliance_pct >= 80 ? "text-[#008932]" : s.compliance_pct >= 50 ? "text-[#92400e]" : "text-[#881a27]"}`}>{s.compliance_pct}%</div>
            </div>
            <div className="h-2 bg-[#f6f6f6] rounded-full overflow-hidden mb-2"><div className={`h-full transition-all ${s.compliance_pct >= 80 ? "bg-[#008932]" : s.compliance_pct >= 50 ? "bg-[#92400e]" : "bg-[#881a27]"}`} style={{ width: `${s.compliance_pct}%` }} /></div>
            <div className="text-[11px] text-[#747474]">{s.users_compliant} / {s.users_total} compliant · {s.trainings_required} trainings required</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Tab = ({ active, onClick, label, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`px-4 py-2 rounded-[12px] text-[13px] font-medium ${active ? "bg-[#e2e8f0] text-[#475569]" : "text-[#747474] hover:bg-[#f6f6f6]"}`}>{label}</button>
);

function TeamTrainingsTab() {
  const { t: tr } = useI18n();
  const [team, setTeam] = useState([]);
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      const u = (await api.get("/users")).data;
      // For managers/HR — show direct reports + own
      const my = (await api.get("/auth/me")).data;
      const teamMembers = my.role === "manager"
        ? u.filter((x) => x.manager_id === my.id && x.active !== false)
        : u.filter((x) => x.active !== false && x.role !== "admin");
      setTeam(teamMembers);
      const all = [];
      for (const t of teamMembers) {
        const r = await api.get(`/training-assignments?user_id=${t.id}`);
        all.push({ user: t, assignments: r.data });
      }
      setItems(all);
    })();
  }, []);

  return (
    <div className="flex flex-col gap-3">
      <div className="text-[13px] text-[#747474]">Status de treinamentos do seu time. Verde = todos completos, amarelo = em andamento, vermelho = pendentes em atraso.</div>
      {items.length === 0 ? <div className="sw-card text-center py-12 text-[#747474]">{tr("train.no_team")}</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map(({ user, assignments }) => {
            const total = assignments.length;
            const completed = assignments.filter((a) => a.status === "completed").length;
            const overdue = assignments.filter((a) => a.status !== "completed" && a.due_date && new Date(a.due_date) < new Date()).length;
            const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
            const color = pct === 100 ? "#008932" : overdue > 0 ? "#881a27" : "#92400e";
            return (
              <div key={user.id} data-testid={`team-row-${user.id}`} className="sw-card !p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-medium text-[14px]">{user.name}</div>
                    <div className="text-[11px] text-[#747474]">{user.role} · {user.job_title || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[22px] font-bold" style={{ color }}>{pct}%</div>
                    <div className="text-[10px] text-[#747474]">{completed} / {total} done</div>
                  </div>
                </div>
                <div className="h-2 bg-[#f6f6f6] rounded-full overflow-hidden mb-2"><div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} /></div>
                {overdue > 0 && <div className="text-[11px] text-[#881a27] font-medium">⚠ {overdue} em atraso</div>}
                {assignments.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#f6f6f6] flex flex-col gap-1 max-h-[140px] overflow-y-auto">
                    {assignments.map((a) => {
                      const od = a.status !== "completed" && a.due_date && new Date(a.due_date) < new Date();
                      return (
                        <div key={a.id} className="flex items-center justify-between text-[11px]">
                          <span className="truncate flex-1 mr-2">{a.training_name}</span>
                          <span className={`shrink-0 px-1.5 py-0.5 rounded-[5px] text-[9px] ${a.status === "completed" ? "bg-[#b8e6cd] text-[#0b430b]" : od ? "bg-[#e5c1c1] text-[#881a27]" : "bg-[#f6f6f6] text-[#747474]"}`}>{a.status}{od ? " · overdue" : ""}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Trainings() {
  const { t: tr } = useI18n();
  const { user } = useAuth();
  const [tab, setTab] = useState(user?.role === "employee" ? "mine" : "catalog");
  const [assigning, setAssigning] = useState(null);
  const canManage = ["manager", "hr", "ceo", "admin"].includes(user?.role);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><GraduationCap size={22} /></div>
        <div>
          <h1 className="sw-heading text-[28px]">{tr("train.title")}</h1>
          <p className="text-[14px] text-[#747474]">{tr("train.subtitle")}</p>
        </div>
      </div>
      <div className="flex gap-2 border-b border-[#eeeded] pb-3">
        <Tab active={tab === "mine"} onClick={() => setTab("mine")} label={tr("train.tab_mine")} testid="tab-my-trainings" />
        {canManage && <Tab active={tab === "team"} onClick={() => setTab("team")} label={tr("train.team_status")} testid="tab-team-trainings" />}
        {canManage && <Tab active={tab === "catalog"} onClick={() => setTab("catalog")} label={tr("train.tab_catalog")} testid="tab-catalog" />}
        {canManage && <Tab active={tab === "compliance"} onClick={() => setTab("compliance")} label={tr("train.tab_compliance")} testid="tab-compliance" />}
      </div>
      {tab === "mine" && <MyTrainingsTab />}
      {tab === "team" && <TeamTrainingsTab />}
      {tab === "catalog" && <CatalogTab canManage={canManage} onAssign={(t) => setAssigning(t)} />}
      {tab === "compliance" && <ComplianceTab />}
      {assigning && <AssignModal training={assigning} onClose={() => setAssigning(null)} onSaved={() => setAssigning(null)} />}
    </div>
  );
}

function TrainingDetailModal({ training, onClose }) {
  const { t: tr } = useI18n();
  const [assignments, setAssignments] = useState([]);
  useEffect(() => {
    api.get(`/training-assignments?training_id=${training.id}`).then((r) => setAssignments(r.data || [])).catch(() => {});
  }, [training.id]);
  const completed = assignments.filter((a) => a.status === "completed").length;
  const total = assignments.length;
  const pct = total ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="training-detail-modal" className="bg-white rounded-[20px] max-w-[600px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#475569] font-semibold">{training.category?.replace("_", " ")}</div>
            <h3 className="sw-heading text-[22px] mt-1">{training.name}</h3>
            <div className="text-[11px] text-[#94a3b8] mt-1 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1"><Clock size={11} /> {training.duration_minutes} min</span>
              {training.mandatory_for_roles?.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-[5px] bg-[#fef3c7] text-[#92400e]">Mandatory: {training.mandatory_for_roles.join(", ")}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-[#94a3b8]" data-testid="training-detail-close"><X size={20} /></button>
        </div>

        <p className="text-[13.5px] text-[#475569] mt-2 whitespace-pre-line">{training.description || "—"}</p>

        {training.external_url && (
          <a href={training.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-[13px] text-[#475569] hover:underline">
            <ExternalLink size={14} /> Open course material
          </a>
        )}

        {total > 0 && (
          <div className="mt-5 pt-4 border-t border-[#e2e8f0]">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#475569] font-semibold mb-2">Assignments · {completed}/{total} completed ({pct}%)</div>
            <div className="h-2 bg-[#f1f5f9] rounded-full overflow-hidden mb-3">
              <div className="h-full bg-[#475569]" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto">
              {assignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-[12px] py-1.5 border-b border-[#f1f5f9] last:border-0">
                  <span className="text-[#1e293b]">{a.user_name || a.user_id}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.status === "completed" ? "bg-emerald-100 text-emerald-700" : a.status === "overdue" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
